import React, { useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    TextField,
    Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { schedulePalette } from "../components/PageLayout";
import { useAuth } from "../contexts/AuthContext";
import { useUI } from "../contexts/UIContext";

export default function Login() {
    const [mode, setMode] = useState("login");
    const [credentials, setCredentials] = useState({ username: "", password: "" });
    const [registration, setRegistration] = useState({
        aziendaNome: "",
        email: "",
        password: "",
        confirmPassword: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const navigate = useNavigate();
    const { login, register } = useAuth();
    const { showNotification } = useUI();

    const isRegisterMode = mode === "register";

    const handleFieldChange = (field) => (event) => {
        setCredentials((prev) => ({ ...prev, [field]: event.target.value }));
    };

    const handleRegistrationFieldChange = (field) => (event) => {
        setRegistration((prev) => ({ ...prev, [field]: event.target.value }));
    };

    const switchMode = (nextMode) => {
        setMode(nextMode);
        setError("");
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSubmitting(true);
        setError("");

        try {
            if (isRegisterMode) {
                if (registration.password !== registration.confirmPassword) {
                    throw new Error("Le password non coincidono");
                }

                await register(registration);
                showNotification("Registrazione completata con successo", "success");
            } else {
                await login(credentials);
                showNotification("Benvenuto nella piattaforma", "success");
            }
            navigate("/");
        } catch (loginError) {
            const message =
                loginError?.response?.data?.message ||
                loginError?.message ||
                (isRegisterMode ? "Registrazione non valida" : "Credenziali non valide");
            setError(message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
                background:
                    "linear-gradient(135deg, rgba(214, 234, 248, 0.95) 0%, rgba(236, 240, 241, 0.90) 50%, rgba(253, 235, 208, 0.92) 100%)",
            }}
        >
            <Grid container maxWidth="md" spacing={3}>
                <Grid item xs={12} md={5}>
                    <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.05, mb: 1.5, color: schedulePalette.ink }}>
                        ShiftFlow
                    </Typography>
                    <Typography sx={{ color: schedulePalette.muted }}>
                        Suite SaaS per la gestione turni, ferie, dipendenti, amministrazione e pagamenti in un unico workspace.
                    </Typography>
                </Grid>
                <Grid item xs={12} md={7}>
                    <Card sx={{ borderTop: `4px solid ${schedulePalette.ocean}` }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, color: schedulePalette.ink }}>
                                {isRegisterMode ? "Registrazione piattaforma" : "Accesso piattaforma"}
                            </Typography>
                            <Typography color="text.secondary" sx={{ mb: 2.5 }}>
                                {isRegisterMode
                                    ? "Crea la tua azienda SaaS e accedi subito alla piattaforma."
                                    : "Inserisci le tue credenziali aziendali."}
                            </Typography>

                            <Box component="form" onSubmit={handleSubmit}>
                                <Grid container spacing={2}>
                                    {isRegisterMode ? (
                                        <>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Nome azienda"
                                                    value={registration.aziendaNome}
                                                    onChange={handleRegistrationFieldChange("aziendaNome")}
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Email"
                                                    type="email"
                                                    value={registration.email}
                                                    onChange={handleRegistrationFieldChange("email")}
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Password"
                                                    type="password"
                                                    value={registration.password}
                                                    onChange={handleRegistrationFieldChange("password")}
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Conferma password"
                                                    type="password"
                                                    value={registration.confirmPassword}
                                                    onChange={handleRegistrationFieldChange("confirmPassword")}
                                                    required
                                                />
                                            </Grid>
                                        </>
                                    ) : (
                                        <>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Username"
                                                    value={credentials.username}
                                                    onChange={handleFieldChange("username")}
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Password"
                                                    type="password"
                                                    value={credentials.password}
                                                    onChange={handleFieldChange("password")}
                                                    required
                                                />
                                            </Grid>
                                        </>
                                    )}
                                    {error && (
                                        <Grid item xs={12}>
                                            <Alert severity="error">{error}</Alert>
                                        </Grid>
                                    )}
                                    <Grid item xs={12}>
                                        <Button type="submit" fullWidth variant="contained" disabled={submitting}>
                                            {submitting ? (
                                                <CircularProgress size={22} color="inherit" />
                                            ) : isRegisterMode ? (
                                                "Registrati"
                                            ) : (
                                                "Login"
                                            )}
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            fullWidth
                                            variant="text"
                                            onClick={() => switchMode(isRegisterMode ? "login" : "register")}
                                            disabled={submitting}
                                        >
                                            {isRegisterMode
                                                ? "Hai già un account? Torna al login"
                                                : "Non hai un account? Registrati"}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}
